import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { activeNetworkSelector, userSelector } from '../../../../redux/selectors';
import { useLazyGetContactsQuery } from '../../../../services/contactsV2';
import { useLazyGetSharedListsQuery } from '../../../../services/contacts';
import { trackEvent } from '../../../../helpers/analytics';
import { isInternalStaff } from '../../../../helpers/isInternalStaff';

export const useSearchResults = (onOpenChange) => {
  const [search, setSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const history = useHistory();
  const activeNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);
  const [getContacts] = useLazyGetContactsQuery();
  const [getSharedLists] = useLazyGetSharedListsQuery();
  const abortControllerRef = useRef(null);

  const isGetroUser = isInternalStaff(user?.email);

  const fetchData = useCallback(
    async (query) => {
      if (!activeNetwork || !query) {
        setContacts([]);
        setLists([]);
        setCollections([]);
        return;
      }

      setLoading(true);
      try {
        const contactsPromise = getContacts({
          filters: {
            keywords: [query],
          },
          page: 1,
        });
        const listsPromise = getSharedLists({
          keyword: query,
          page: 1,
        });

        abortControllerRef.current = {
          abort: () => {
            contactsPromise.abort?.();
            listsPromise.abort?.();
          },
        };

        const [contactsResult, listsResult] = await Promise.all([contactsPromise.unwrap(), listsPromise.unwrap()]);

        const contactsData = contactsResult.ids.map((id) => {
          const contact = contactsResult.entities[id];
          const position = contact.primaryActivePosition
            ? `${contact.primaryActivePosition.jobTitle}${
                contact.primaryActivePosition.companyName ? ` at ${contact.primaryActivePosition.companyName}` : ''
              }`
            : '';

          return {
            label: contact.fullName,
            id: contact.id,
            url: `/networks/${activeNetwork.slug}/contacts/${contact.id}`,
            icon: contact.profilePicUrl,
            email: contact.email,
            position,
            type: 'contact',
          };
        });

        const listsData = listsResult.sharedLists.map((list) => ({
          label: list.name,
          id: list.id,
          url: `/networks/${activeNetwork.slug}/list/${list.id}/contacts`,
          path: `/${list.folderPath}`,
          kind: list.kind,
          type: 'list',
        }));

        // Search collections if user is a Getro team member
        let collectionsData = [];
        if (isGetroUser && user?.managingCollections) {
          const searchLower = query.toLowerCase();
          collectionsData = user.managingCollections
            .filter((collection) => {
              const nameMatch = collection.name?.toLowerCase().includes(searchLower);
              const slugMatch = collection.slug?.toLowerCase().includes(searchLower);
              return nameMatch || slugMatch;
            })
            .map((collection) => ({
              label: collection.name,
              id: collection.id,
              url: `/networks/${collection.slug}`,
              slug: collection.slug,
              type: 'collection',
            }));
        }

        setContacts(contactsData);
        setLists(listsData);
        setCollections(collectionsData);
        setHasSearched(true);

        trackEvent('universal_search:search', { query });
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          return;
        }
        setContacts([]);
        setLists([]);
        setCollections([]);
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    },
    [activeNetwork, getContacts, getSharedLists, isGetroUser, user],
  );

  useEffect(() => {
    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (search.length > 0) {
      const timer = setTimeout(() => {
        fetchData(search);
      }, 300);

      return () => {
        clearTimeout(timer);
        // Abort the request if component unmounts or search changes
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }

    // Reset state when search is cleared
    setContacts([]);
    setLists([]);
    setCollections([]);
    setLoading(false);
    setHasSearched(false);
    return undefined;
  }, [search, fetchData]);

  const handleSelect = useCallback(
    (url, resultType) => {
      trackEvent('universal_search:search_result_click', { resultType });

      history.push(url);
      onOpenChange(false);
      setSearch('');
      setActiveTab('all');
      setHasSearched(false);
    },
    [history, onOpenChange],
  );

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  // Calculate filtered results based on active tab
  const filteredContacts = activeTab === 'all' || activeTab === 'contacts' ? contacts : [];
  const filteredLists = activeTab === 'all' || activeTab === 'lists' ? lists : [];
  const filteredCollections = activeTab === 'all' || activeTab === 'collections' ? collections : [];

  // Calculate counts
  const totalCount = contacts.length + lists.length + collections.length;
  const contactsCount = contacts.length;
  const listsCount = lists.length;
  const collectionsCount = collections.length;

  return {
    search,
    setSearch,
    contacts: filteredContacts,
    lists: filteredLists,
    collections: filteredCollections,
    loading,
    handleSelect,
    activeTab,
    handleTabChange,
    totalCount,
    contactsCount,
    listsCount,
    collectionsCount,
    hasSearched,
    isGetroUser,
  };
};
